/**
 * scheduleBookings.ts
 * Frontend service for ScheduleTask and MachineBooking API calls.
 *
 * Mirrors backend/src/repositories/scheduleTaskRepository.ts and machineBookingRepository.ts.
 * Phase 2, Item 1 — basic CRUD scaffold.
 *
 * All API calls go through apiClient (MSAL bearer + base URL + service-status capture).
 */

import apiClient from './apiClient';
import type {
  ScheduleTask,
  MachineBooking,
  MachineBookingCalendar,
  MachineBookingEventType,
  ConflictSummary,
} from '../types/schedule.js';

// =============================================================================
// ScheduleTask API
// =============================================================================

export async function listScheduleTasks(): Promise<ScheduleTask[]> {
  const response = await apiClient.get<ScheduleTask[]>('/schedule-tasks');
  return response.data;
}

export async function getScheduleTasksByQuote(quoteKey: string): Promise<ScheduleTask[]> {
  const response = await apiClient.get<ScheduleTask[]>(`/schedule-tasks?quoteKey=${quoteKey}`);
  return response.data;
}

export async function getScheduleTasksByWorkOrder(workOrderKey: string): Promise<ScheduleTask[]> {
  const response = await apiClient.get<ScheduleTask[]>(`/schedule-tasks?workOrderKey=${workOrderKey}`);
  return response.data;
}

export async function createScheduleTask(
  task: Omit<ScheduleTask, 'ScheduleTaskKey' | 'sysCreateUser' | 'sysCreateDate' | 'sysLastModifiedUser' | 'sysLastModifiedDate' | 'sysLastAction'>
): Promise<ScheduleTask> {
  const response = await apiClient.post<ScheduleTask>('/schedule-tasks', task);
  return response.data;
}

export async function updateScheduleTask(
  key: string,
  updates: Partial<Omit<ScheduleTask, 'ScheduleTaskKey' | 'SourceType' | 'sysCreateUser' | 'sysCreateDate' | 'sysLastModifiedUser' | 'sysLastModifiedDate' | 'sysLastAction'>>
): Promise<ScheduleTask> {
  const response = await apiClient.patch<ScheduleTask>(`/schedule-tasks/${key}`, updates);
  return response.data;
}

export async function deleteScheduleTask(key: string): Promise<void> {
  await apiClient.delete(`/schedule-tasks/${key}`);
}

// =============================================================================
// Machine Schedule Calendar (Phase 2, Item 4)
// =============================================================================

/**
 * Fetch all active MachineBookings that overlap the given date range.
 * Joined with Machine and ScheduleTask fields — ready for the calendar grid.
 *
 * Backend: GET /api/schedule/machine-bookings?startDate=&endDate=[&scenarioKey=]
 *
 * When `scenarioKey` is provided (FEAT-2026-0007 6A.4 / 6B.5), the response
 * overlays that draft's Tentative rows on top of the committed baseline so
 * the scenario grid shows both committed bars and this scenario's proposed bars.
 *
 * When `includeOtherScenarios` is true ("show the blocker"), the backend drops
 * the scenario overlay and also returns OTHER drafts' Tentative bookings, each
 * flagged `isOtherScenario=true`, so the scenario grid can render what's
 * reserving a slot that otherwise looks empty. Only the scenario Machine grid
 * passes this; the live Machine Schedule / Master Gantt leave it unset.
 */
export async function fetchMachineBookings(range: {
  startDate: string; // 'YYYY-MM-DD'
  endDate: string;   // 'YYYY-MM-DD'
  /** Optional scenario key — when supplied, overlays that draft on the baseline. */
  scenarioKey?: string;
  /** When true, also return other drafts' tentative bookings (isOtherScenario). */
  includeOtherScenarios?: boolean;
}): Promise<MachineBookingCalendar[]> {
  const params = new URLSearchParams({
    startDate: range.startDate,
    endDate: range.endDate,
  });
  if (range.scenarioKey) params.set('scenarioKey', range.scenarioKey);
  if (range.includeOtherScenarios) params.set('includeOtherScenarios', 'true');
  const response = await apiClient.get<MachineBookingCalendar[]>(
    `/schedule/machine-bookings?${params}`,
  );
  return response.data;
}

// =============================================================================
// MachineBooking API
// =============================================================================

export async function listMachineBookings(): Promise<MachineBooking[]> {
  const response = await apiClient.get<MachineBooking[]>('/machine-bookings');
  return response.data;
}

export async function getMachineBookingsByMachine(machineKey: string): Promise<MachineBooking[]> {
  const response = await apiClient.get<MachineBooking[]>(`/machine-bookings?machineKey=${machineKey}`);
  return response.data;
}

export async function getMachineBookingsByTask(scheduleTaskKey: string): Promise<MachineBooking[]> {
  const response = await apiClient.get<MachineBooking[]>(`/machine-bookings?scheduleTaskKey=${scheduleTaskKey}`);
  return response.data;
}

export async function getMachineBookingsByScenario(scenarioKey: string): Promise<MachineBooking[]> {
  const response = await apiClient.get<MachineBooking[]>(`/machine-bookings?scenarioKey=${scenarioKey}`);
  return response.data;
}

export async function createMachineBooking(
  booking: Omit<MachineBooking, 'MachineBookingKey' | 'sysCreateUser' | 'sysCreateDate' | 'sysLastModifiedUser' | 'sysLastModifiedDate' | 'sysLastAction'>
): Promise<MachineBooking> {
  const response = await apiClient.post<MachineBooking>('/machine-bookings', booking);
  return response.data;
}

export async function updateMachineBooking(
  key: string,
  updates: Partial<Omit<MachineBooking, 'MachineBookingKey' | 'ScheduleTaskKey' | 'MachineKey' | 'Source' | 'ScenarioKey' | 'sysCreateUser' | 'sysCreateDate' | 'sysLastModifiedUser' | 'sysLastModifiedDate' | 'sysLastAction'>>
): Promise<MachineBooking> {
  const response = await apiClient.patch<MachineBooking>(`/machine-bookings/${key}`, updates);
  return response.data;
}

/**
 * Soft-delete a single MachineBooking (BookingDetailDrawer "Delete" — the only
 * removal path for manual Maintenance/Calibration/OutOfService events).
 *
 * Backend: DELETE /api/schedule/bookings/:key  (BUG-2026-0119 — the previous
 * `/machine-bookings/:key` endpoint was Phase 2 Item 1 scaffold that was never
 * mounted, so every delete attempt 404'd and the bar snapped back.)
 */
export async function deleteMachineBooking(key: string): Promise<void> {
  try {
    await apiClient.delete(`/schedule/bookings/${key}`);
  } catch (err: any) {
    const msg = err?.response?.data?.error
      ?? `Failed to delete booking ${key}: ${err?.message ?? 'request failed'}`;
    throw new Error(msg);
  }
}

// =============================================================================
// Drag-resize booking date range (Phase 2, Item 5)
// =============================================================================

// =============================================================================
// Manual event creation — Phase 2, Item 6
// =============================================================================

export interface CreateManualEventRequest {
  machineKey: string;
  eventType: MachineBookingEventType;
  startDate: string; // 'YYYY-MM-DD'
  endDate: string;   // 'YYYY-MM-DD'
  notes?: string;
  /**
   * G2-6: minutes of machine time the event blocks on EACH covered day
   * (1..480). Omitted = full-day block (legacy semantics).
   */
  eventMinutes?: number;
  /**
   * G2-6: opt-in overlap warn — when true and the event overlaps existing
   * bookings, the API responds 409 needsConfirm with the collision list
   * (thrown here as ManualEventConflictError) instead of silently creating
   * a hard conflict. Resend with force: true to create anyway.
   */
  confirmCheck?: boolean;
  force?: boolean;
}

/** One overlapping booking from the G2-6 overlap warn (409 needsConfirm). */
export interface ManualEventCollision {
  MachineBookingKey: string;
  StartDate: string;
  EndDate: string;
  Status: string;
  EventType: string | null;
  TaskName: string | null;
}

/**
 * G2-6: thrown by createManualEvent when the API answers 409 needsConfirm —
 * carries the collision list so the dialog can render the warning and offer
 * "Create anyway" (advisory, never blocks).
 */
export class ManualEventConflictError extends Error {
  readonly needsConfirm = true;
  readonly collisions: ManualEventCollision[];
  constructor(message: string, collisions: ManualEventCollision[]) {
    super(message);
    this.name = 'ManualEventConflictError';
    this.collisions = collisions;
  }
}

/**
 * POST /api/schedule/bookings/event
 *
 * Creates a standalone manual MachineBooking (Maintenance / Calibration /
 * OutOfService) scoped to a single machine.  Returns the created MachineBooking.
 *
 * G2-6: when payload.confirmCheck is true and the event overlaps existing
 * bookings, throws ManualEventConflictError (409 needsConfirm) with the
 * collision list; resend with force: true to create anyway.
 */
export async function createManualEvent(
  payload: CreateManualEventRequest,
): Promise<MachineBooking> {
  try {
    const response = await apiClient.post<MachineBooking>(
      '/schedule/bookings/event',
      payload,
    );
    return response.data;
  } catch (err: any) {
    const data = err?.response?.data;
    if (err?.response?.status === 409 && data?.needsConfirm === true) {
      throw new ManualEventConflictError(
        data.error ?? 'This event overlaps existing bookings on the machine.',
        (data.collisions ?? []) as ManualEventCollision[],
      );
    }
    const msg = data?.error
      ?? `Failed to create event: ${err?.message ?? 'request failed'}`;
    throw new Error(msg);
  }
}

// =============================================================================
// Drag-resize booking date range (Phase 2, Item 5)
// =============================================================================

// =============================================================================
// Conflict Detection (Phase 2, Item 7)
// =============================================================================

// =============================================================================
// Release Summary (Phase 2, Item 9)
// =============================================================================

export interface QuoteReleaseSummary {
  tasksReleased: number;
  bookingsReleased: number;
}

/**
 * GET /api/schedule/quotes/:quoteKey/release-summary
 *
 * Returns the count of soft-deleted tasks and bookings for a Declined/Withdrawn
 * quote. Used to show a persistent banner on the quote editor page.
 */
export async function getQuoteReleaseSummary(
  quoteKey: string,
): Promise<QuoteReleaseSummary> {
  const response = await apiClient.get<QuoteReleaseSummary>(
    `/schedule/quotes/${quoteKey}/release-summary`,
  );
  return response.data;
}

/**
 * Fetch hard and soft conflicts for the given date window.
 *
 * Backend: GET /api/schedule/conflicts?startDate=&endDate=[&scenarioKey=]
 * Returns: { hard: HardConflict[], soft: SoftConflict[] }
 *
 * When `scenarioKey` is provided (FEAT-2026-0007 6B.6), the backend overlays
 * that draft's Tentative rows on the committed baseline so conflicts are detected
 * within the scenario context (not just the live schedule).
 */
export async function fetchScheduleConflicts(range: {
  startDate: string;   // 'YYYY-MM-DD'
  endDate: string;     // 'YYYY-MM-DD'
  /** Optional scenario key — when supplied, overlays that draft on the baseline. */
  scenarioKey?: string;
}): Promise<ConflictSummary> {
  const params = new URLSearchParams({
    startDate: range.startDate,
    endDate: range.endDate,
  });
  if (range.scenarioKey) params.set('scenarioKey', range.scenarioKey);
  const response = await apiClient.get<ConflictSummary>(
    `/schedule/conflicts?${params}`,
  );
  return response.data;
}

/**
 * New placement (date range and/or machine) for a MachineBooking.
 * At least one field is required. Dates are 'YYYY-MM-DD'; MachineKey is a GUID.
 */
export interface BookingPlacement {
  StartDate?: string;
  EndDate?: string;
  /** Move the booking to a different machine (drag across rows). */
  MachineKey?: string;
}

/** Why a placement needs confirmation (overlap and/or weekend/holiday start). */
export interface PlacementConfirmInfo {
  conflict: boolean;
  weekend: boolean;
  /** Earliest free working-day start the caller can offer as a one-click fix. */
  earliestFree: string;
  /** Human label for the booking it overlaps (task + dates), when a conflict. */
  conflictLabel?: string | null;
}

/**
 * Thrown by `updateBookingPlacement` when called with `{ check: true }` and the
 * placement hits a soft barrier (overlaps a booking and/or starts on a
 * weekend/holiday). The caller catches this to show a "book anyway / use
 * earliest free" confirm dialog, then retries with `{ force: true }`.
 */
export class PlacementNeedsConfirmError extends Error {
  readonly info: PlacementConfirmInfo;
  constructor(info: PlacementConfirmInfo) {
    super('Placement needs confirmation');
    this.name = 'PlacementNeedsConfirmError';
    this.info = info;
  }
}

/**
 * Update the placement of a MachineBooking — date range AND/OR machine — in a
 * single atomic call (so a diagonal drag = one request).
 *
 * Backend: PUT /api/schedule/bookings/:key
 * Body:    { StartDate?, EndDate?, MachineKey? }  — at least one required.
 * Returns: the updated MachineBooking row.
 *
 * `opts.check` opts into the warn-and-confirm gate: the backend evaluates the
 * placement for overlap + weekend/holiday and, if either is hit, throws
 * `PlacementNeedsConfirmError` instead of persisting. `opts.force` tells the
 * backend the user already confirmed, so it persists regardless. With no opts
 * the call behaves exactly as before (persist, no check) — used by the
 * standalone Machine Schedule view and Phase-2 resize.
 *
 * Throws:  PlacementNeedsConfirmError on a soft barrier (when check),
 *          or Error with the API message on other 4xx/5xx.
 */
export async function updateBookingPlacement(
  key: string,
  placement: BookingPlacement,
  opts?: { check?: boolean; force?: boolean },
): Promise<MachineBooking> {
  const body: Record<string, unknown> = { ...placement };
  if (opts?.check) body.ConfirmCheck = true;
  if (opts?.force) body.Force = true;
  try {
    const response = await apiClient.put<MachineBooking>(
      `/schedule/bookings/${key}`,
      body,
    );
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 409 && err?.response?.data?.needsConfirm) {
      const d = err.response.data;
      throw new PlacementNeedsConfirmError({
        conflict: Boolean(d.conflict),
        weekend: Boolean(d.weekend),
        earliestFree: d.earliestFree as string,
        conflictLabel: (d.conflictLabel as string | null | undefined) ?? null,
      });
    }
    const msg = err?.response?.data?.error
      ?? `Failed to update booking ${key}: ${err?.message ?? 'request failed'}`;
    throw new Error(msg);
  }
}

/**
 * Back-compat alias for the original drag-resize call. Delegates to
 * updateBookingPlacement so there is a single update path.
 */
export async function resizeMachineBookingDateRange(
  key: string,
  dates: { StartDate?: string; EndDate?: string },
): Promise<MachineBooking> {
  return updateBookingPlacement(key, dates);
}

// =============================================================================
// Machine capabilities (drop-target resolution) — Machine Schedule UX overhaul
// =============================================================================

export interface MachineCapability {
  MachineKey: string;
  TaskTypeKey: string;
}

/**
 * Fetch every active machine ↔ task-type capability pair.
 * The grid builds a taskTypeKey → Set<machineKey> map to decide which machine
 * rows are valid drop targets when a booking is dragged across rows.
 *
 * Backend: GET /api/schedule/machine-capabilities
 */
export async function fetchMachineCapabilities(): Promise<MachineCapability[]> {
  const response = await apiClient.get<MachineCapability[]>(
    '/schedule/machine-capabilities',
  );
  return response.data;
}
